import { Anchor, Button, Input } from '@byecode/ui'
import { ErrorMessage, TagIcon, useAtomAction, useAtomData } from '@lighthouse/shared'
import { find } from 'rambda'
import React, { useCallback, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useAsyncFn } from 'react-use'

import { fetchOtherPlatformsAtom } from '@/atoms/application/action'
import { otherPlatformListAtom } from '@/atoms/application/state'
import {
    otherPlatformBackgroundColorMap,
    otherPlatformColorMap,
    otherPlatformDescribeMap,
    otherPlatformNameMap
} from '@/containers/OtherPlatforms/constant'
import * as srv from '@/services'

import { PLACEHOLDER_PASSWORD } from '../constants'
import * as SC from '../styles'
import type { AliyunAccountFormState } from './type'

interface AliyunAccountProps {
    onClose?: () => void
}

const initData: AliyunAccountFormState = {
    id: '',
    secret: '',
    accessKey: ''
}
const AliyunAccount: React.FunctionComponent<AliyunAccountProps> = ({ onClose }) => {
    const otherPlatforms = useAtomData(otherPlatformListAtom)
    const { run: fetchOtherPlatforms } = useAtomAction(fetchOtherPlatformsAtom)
    const account = useMemo(() => {
        const otherPlatform = find(item => item.type === 'ALIYUN_SMS', otherPlatforms ?? [])
        return otherPlatform?.configurationComplete
            ? { ...otherPlatform?.config, secret: PLACEHOLDER_PASSWORD, accessKey: PLACEHOLDER_PASSWORD, id: otherPlatform.id }
            : { ...initData, id: otherPlatform?.id }
    }, [otherPlatforms])

    const {
        control,
        register,
        handleSubmit,
        getValues,
        formState: { errors }
    } = useForm({
        mode: 'onSubmit',
        defaultValues: account
    })

    const [{ loading: submitLoading }, doUpdateIntegration] = useAsyncFn(srv.updateIntegration)
    const handleUpdate = useCallback(
        async (params: AliyunAccountFormState) => {
            const { secret, accessKey, id } = params
            if (!id || submitLoading) {
                return
            }
            const res = await doUpdateIntegration({
                type: 'ALIYUN_SMS',
                id,
                config: {
                    secret: secret === PLACEHOLDER_PASSWORD ? undefined : secret,
                    accessKey: accessKey === PLACEHOLDER_PASSWORD ? undefined : accessKey
                }
            })
            if (res) {
                fetchOtherPlatforms()
                onClose?.()
            }
        },
        [doUpdateIntegration, fetchOtherPlatforms, onClose, submitLoading]
    )

    const handleInviteSubmit = useCallback(
        (ev: React.FormEvent) => {
            ev.preventDefault()
            handleSubmit(handleUpdate)()
        },
        [handleSubmit, handleUpdate]
    )

    return (
        <SC.Container onSubmit={handleInviteSubmit}>
            <SC.Content>
                <TagIcon
                    size={48}
                    iconSize={32}
                    icon="aliyun"
                    radius={6}
                    iconColor={otherPlatformColorMap['ALIYUN_SMS']}
                    background={otherPlatformBackgroundColorMap['ALIYUN_SMS']}
                />
                <SC.Title>{otherPlatformNameMap['ALIYUN_SMS']}</SC.Title>
                <SC.Item>
                    <SC.Description>{otherPlatformDescribeMap['ALIYUN_SMS']}</SC.Description>
                </SC.Item>
                <SC.Tip>
                    <SC.Icon color="var(--color-main)" type="WarningCircle" />
                    <SC.Text>如果您完善授权时遇到问题，请查看帮助文档：</SC.Text>
                    <Anchor
                        target="_blank"
                        style={{ fontWeight: 600, color: 'var(--color-main)' }}
                        to="https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/qrt0xo5hg02682zs?singleDoc#"
                    >
                        阿里云授权配置
                    </Anchor>
                </SC.Tip>
                <SC.Item>
                    <SC.Text>
                        Access Key
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="accessKey" errors={errors}>
                        <Controller
                            name="accessKey"
                            control={control}
                            rules={{ required: '不能为空' }}
                            render={({ field }) => {
                                return (
                                    <Input
                                        autoComplete="new-password"
                                        type="password"
                                        onFocus={() => {
                                            if (field.value === PLACEHOLDER_PASSWORD) {
                                                field.onChange('')
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!field.value.trim()) {
                                                field.onChange(PLACEHOLDER_PASSWORD)
                                            }
                                        }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        请登录
                        <Anchor
                            target="_blank"
                            style={{ color: 'var(--color-main)' }}
                            to="https://homenew.console.aliyun.com/home/dashboard"
                        >
                            阿里云控制台用户页面
                        </Anchor>
                        -顶部导航企业-选择人员权限管理-选择已有用户或者创建一个新用户，并选择添加权限-添加短信管理全部权限到此用户-在此用户下创建一个AccessKey-获取AccessKey
                        ID复制到Access Key内容中
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>
                        Access Secret
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="secret" errors={errors}>
                        <Controller
                            name="secret"
                            control={control}
                            rules={{ required: '不能为空' }}
                            render={({ field }) => {
                                return (
                                    <Input
                                        autoComplete="new-password"
                                        type="password"
                                        onFocus={() => {
                                            if (field.value === PLACEHOLDER_PASSWORD) {
                                                field.onChange('')
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!field.value.trim()) {
                                                field.onChange(PLACEHOLDER_PASSWORD)
                                            }
                                        }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        请登录
                        <Anchor
                            target="_blank"
                            style={{ color: 'var(--color-main)' }}
                            to="https://homenew.console.aliyun.com/home/dashboard"
                        >
                            阿里云控制台用户页面
                        </Anchor>
                        -顶部导航企业-选择人员权限管理-选择已有用户或者创建一个新用户，并选择添加权限-添加短信管理全部权限到此用户-在此用户下创建一个AccessKey-获取secret复制到Access
                        Secret内容中
                    </SC.Description>
                </SC.Item>
            </SC.Content>
            <SC.Footer>
                <SC.LeftFill />
                <SC.RightFill>
                    <Button size="lg" style={{ width: 104 }} onClick={() => onClose?.()}>
                        取消
                    </Button>
                    <Button size="lg" loading={submitLoading} style={{ width: 104 }} type="primary" htmlType="submit">
                        确定
                    </Button>
                </SC.RightFill>
            </SC.Footer>
        </SC.Container>
    )
}

export default AliyunAccount
